import { useState, useEffect, useCallback, useRef } from 'react'

export interface ContextMenuPosition {
  x: number
  y: number
}

/**
 * Standard hook for context menu behavior:
 * - Closes on outside click (capture phase, works with canvas editors)
 * - Closes on Escape
 * - Clamps position to viewport
 * - Returns open/close helpers and a ref for the menu element
 */
export function useContextMenu<T extends ContextMenuPosition = ContextMenuPosition>(
  opts?: {
    /** Return true for a mousedown target that should NOT close the menu —
     *  e.g. the trigger button, which owns its own open/close (toggle) so the
     *  outside-handler mustn't fight it. Keep it referentially stable. */
    ignoreTarget?: (target: EventTarget | null) => boolean
  },
) {
  const [menu, setMenu] = useState<T | null>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const ignoreTargetRef = useRef(opts?.ignoreTarget)
  ignoreTargetRef.current = opts?.ignoreTarget

  const open = useCallback((state: T) => {
    setMenu(state)
  }, [])

  const close = useCallback(() => {
    setMenu(null)
  }, [])

  // Close on outside click (capture phase so canvas editors can't block it)
  useEffect(() => {
    if (!menu) return
    const handler = (e: MouseEvent) => {
      if (ignoreTargetRef.current?.(e.target)) return
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenu(null)
      }
    }
    document.addEventListener('mousedown', handler, true)
    return () => document.removeEventListener('mousedown', handler, true)
  }, [menu])

  // Close on Escape
  useEffect(() => {
    if (!menu) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenu(null)
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [menu])

  // Close on window blur — a mousedown inside a sandboxed <iframe> (app/preview
  // windows) never reaches this document, so the outside-click handler can't
  // see it; the focus leaving the top window is the only signal we get.
  useEffect(() => {
    if (!menu) return
    const handler = () => setMenu(null)
    window.addEventListener('blur', handler)
    return () => window.removeEventListener('blur', handler)
  }, [menu])

  // Clamp position to viewport after render
  useEffect(() => {
    if (!menu || !menuRef.current) return
    const el = menuRef.current
    const rect = el.getBoundingClientRect()
    const vw = window.innerWidth
    const vh = window.innerHeight
    let x = menu.x
    let y = menu.y
    if (x + rect.width > vw) x = Math.max(0, vw - rect.width - 4)
    if (y + rect.height > vh) y = Math.max(0, vh - rect.height - 4)
    if (x !== menu.x) el.style.left = `${x}px`
    if (y !== menu.y) el.style.top = `${y}px`
  }, [menu])

  return { menu, menuRef, open, close }
}
